<template>
  <div>
    <template v-if="permissions === null">
      <loader />
    </template>
    <template v-else-if="permissions.length === 0">
      {{ $t('user-has-no-permissions') }}
    </template>
    <template v-else>
      <b-table
        ref="selectableTable"
        :fields="permissionsTable.fields"
        striped
        hover
        responsive
        :items="permissions"
        selectable
        selected-variant="success"
        @row-selected="onRowSelected"
        tbodyTrClass="cursor-pointer"
      >
        <template v-slot:cell(status_access)="{ item }">
          <span aria-hidden="true">{{ item.status_access ? '&check;' : '&nbsp;' }}</span>
        </template>

        <template v-slot:cell(control_access)="{ item }">
          <span aria-hidden="true">{{ item.control_access ? '&check;' : '&nbsp;' }}</span>
        </template>

        <template v-slot:cell(settings_access)="{ item }">
          <span aria-hidden="true">{{ item.settings_access ? '&check;' : '&nbsp;' }}</span>
        </template>

        <template v-slot:cell(placeName)="{ item }">
          <span>
            {{ getServiceById(item.service).placeName }}
          </span>
        </template>
        <template v-slot:cell(groupName)="{ item }">
          <span>
            {{ getServiceById(item.service).groupName }}
          </span>
        </template>
        <template v-slot:cell(icon)="{ item }">
          <component
            v-bind:is="getServiceById(item.service).icon"
            :style="{ height: '20px' }"
            :service="getServiceById(item.service)" />
        </template>
        <template v-slot:cell(name)="{ item }">
          <span>
            {{ getServiceById(item.service).name }}
          </span>
        </template>
      </b-table>
    </template>
    <service-permissions-modal-add-form
      ref="servicePermissionsModalAddForm"
      :permissions="permissions"
      @service-picked="servicesPicked"
    />
    <service-permissions-modal-edit-form
      ref="servicePermissionsModalEditForm"
      @access-control-selected="editPermissions"
    />
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config';
import BreadcrumbsManager from '@/mixins/BreadcrumbsManager';
import Loader from '@/components/utils/Loader.vue';
import { mapGetters, mapState } from 'vuex';
import eventBus from '@/components/utils/slideout-panel/eventBus';
import ServicePermissionsModalAddForm
  from '@/components/permissions/ServicePermissionsModalAddForm.vue';
import ServicePermissionsModalEditForm
  from '@/components/permissions/ServicePermissionsModalEditForm.vue';

export default {
  name: 'UserPermissions',
  components: { ServicePermissionsModalEditForm, Loader, ServicePermissionsModalAddForm },
  mixins: [
    BreadcrumbsManager,
  ],
  props: {
    id: {
      type: Number,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      permissions: null,
      breadcrumbs: [
        {
          text: this.$t('users-management'),
          to: {
            name: 'users-management',
          },
        },
        {
          text: `${this.user.user.username} ${this.$t('user-permissions')}`,
        },
      ],
      selectedPermissions: [],
      permissionsTable: {
        fields: [
          'placeName',
          'groupName',
          'icon',
          'name',
          'status_access',
          'control_access',
          'settings_access',
        ],
      },
    };
  },
  computed: {
    ...mapState('snapshot', [
      'currentSnapshot',
    ]),
    ...mapGetters([
      'getServiceById',
    ]),
  },
  methods: {
    onRowSelected(items) {
      this.selectedPermissions = items;
      eventBus.$emit('permissionSelected', items);
    },
    async loadServicePermissions() {
      this.loading = true;
      const url = new URL(
        config.ICOOL.API.USER_SERVICE_PERMISSIONS_PATH(this.currentSnapshot.id, this.user.user.id),
        config.ICOOL.BACKEND_HOST,
      ).href;
      try {
        const response = await axios.get(url);
        this.permissions = response.data;
      } catch (err) {
        this.$log.error(err);
      }
      this.loading = false;
    },
    addPermissionClicked() {
      this.$refs.servicePermissionsModalAddForm.show();
    },
    editPermissionClicked() {
      this.$refs.servicePermissionsModalEditForm.show();
    },
    servicesPicked({ serviceIds, accessControl }) {
      const data = serviceIds.map(serviceId => ({
        service: serviceId,
        user: this.user.user.id,
        ...this.mapAccessControlFormDataToObject(accessControl),
      }));
      this.postPermissions(data);
    },
    editPermissions(accessControlData) {
      const data = this.selectedPermissions.map(p => ({
        id: p.id,
        service: p.service,
        user: p.user,
        ...this.mapAccessControlFormDataToObject(accessControlData),
      }));
      this.postPermissions(data);
    },
    postPermissions(permissions) {
      const url = new URL(
        config.ICOOL.API.USER_SERVICE_PERMISSIONS_PATH(this.currentSnapshot.id, this.user.user.id),
        config.ICOOL.BACKEND_HOST,
      ).href;
      axios
        .post(url, { permissions })
        .then((response) => {
          eventBus.$emit('servicePermissionUpdated');
        })
        .catch((error) => {
          this.$log.error(error);
          reject();
        })
        .finally(() => {
        });
    },
    mapAccessControlFormDataToObject(formData) {
      return {
        status_access: this.$_.includes(formData, 'status'),
        control_access: this.$_.includes(formData, 'control'),
        settings_access: this.$_.includes(formData, 'settings'),
      };
    },
  },
  mounted() {
    this.loadServicePermissions();
  },
  created() {
    eventBus.$on('servicePermissionUpdated', this.loadServicePermissions);
    eventBus.$on('addPermissionClicked', this.addPermissionClicked);
    eventBus.$on('editPermissionClicked', this.editPermissionClicked);
  },
  destroyed() {
    eventBus.$off('servicePermissionUpdated');
    eventBus.$off('addPermissionClicked');
    eventBus.$off('editPermissionClicked');
  },
};
</script>

<style scoped>

</style>
